import React, { useState } from 'react';
import { Image, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Link, router } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { Logo } from '@/components/logo';
import { PressableScale } from '@/components/ui/pressable-scale';
import {
  DropdownMenu,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { LoginDialog } from '@/components/public/login-dialog';
import { useAuthStore } from '@/store/authStore';
import { isProviderUser } from '@/utils/user-role';

/**
 * Header da marca Controlapag para as páginas públicas que o cliente vê do
 * prestador. Dá identidade da plataforma sem competir com a cor de marca do
 * prestador. Quando há sessão, mostra o nome com um dropdown (perfil,
 * agendamentos, sair); sem sessão, um botão "Entrar".
 */
export function PublicBrandHeader({ onBack }: { onBack?: () => void }) {
  const insets = useSafeAreaInsets();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [loginOpen, setLoginOpen] = useState(false);

  const isProvider = isProviderUser(user);
  const profileHref = isProvider
    ? '/(tabs)/(provider)/profile'
    : '/(tabs)/(client)/account';
  const appointmentsHref = isProvider
    ? '/(tabs)/(provider)/home'
    : '/(tabs)/(client)/appointments';

  const initial = user?.username?.slice(0, 1).toUpperCase() ?? '?';

  return (
    <View
      className="border-b border-neutral-100 bg-card"
      style={{ paddingTop: insets.top }}
    >
      <View className="h-14 flex-row items-center justify-between px-4">
        <View className="flex-row items-center gap-2">
          {onBack && (
            <Pressable
              onPress={onBack}
              className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100"
              accessibilityLabel="Voltar"
            >
              <Ionicons className="text-neutral-600" name="arrow-back" size={19} />
            </Pressable>
          )}
          {/* O logo leva à página inicial da Controlapag. */}
          <Link href="/" asChild>
            <Pressable accessibilityRole="link" accessibilityLabel="Controlapag, página inicial">
              <Logo variant="full" width={120} height={19} color="tint" />
            </Pressable>
          </Link>
        </View>

        {user ? (
          <DropdownMenu
            align="end"
            trigger={
              <View className="flex-row items-center gap-2">
                <View
                  className="h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary"
                  style={{ borderCurve: 'continuous' }}
                >
                  {user.image ? (
                    <Image
                      source={{ uri: user.image }}
                      aria-hidden
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <ThemedText className="text-sm font-bold text-white">
                      {initial}
                    </ThemedText>
                  )}
                </View>
                <ThemedText
                  numberOfLines={1}
                  className="max-w-[110px] text-sm font-semibold text-neutral-700"
                >
                  {user.username}
                </ThemedText>
                <Ionicons className="text-neutral-500" name="chevron-down" size={14} />
              </View>
            }
          >
            <DropdownMenuItem onPress={() => router.push(profileHref as never)}>
              <View className="flex-row items-center gap-2 py-0.5">
                <Ionicons className="text-neutral-600" name="person-outline" size={16} />
                <ThemedText className="text-sm text-neutral-700">
                  Meu perfil
                </ThemedText>
              </View>
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={() => router.push(appointmentsHref as never)}
            >
              <View className="flex-row items-center gap-2 py-0.5">
                <Ionicons className="text-neutral-600" name="calendar-outline" size={16} />
                <ThemedText className="text-sm text-neutral-700">
                  Meus agendamentos
                </ThemedText>
              </View>
            </DropdownMenuItem>
            <View className="my-1 h-px bg-neutral-100" />
            <DropdownMenuItem onPress={() => void logout()}>
              <View className="flex-row items-center gap-2 py-0.5">
                <Ionicons className="text-error-600" name="log-out-outline" size={16} />
                <ThemedText className="text-sm font-medium text-red-600">
                  Sair
                </ThemedText>
              </View>
            </DropdownMenuItem>
          </DropdownMenu>
        ) : (
          <PressableScale
            haptic
            onPress={() => setLoginOpen(true)}
            className="flex-row items-center gap-1.5 px-2 py-2"
          >
            <Ionicons name="log-in-outline" size={18} className="text-primary" />
            <ThemedText className="text-sm font-bold text-primary">
              Entrar
            </ThemedText>
          </PressableScale>
        )}
      </View>

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </View>
  );
}
